import React, { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Dialog, DialogContent, DialogPortal } from 'components/ui/dialog'
import { Cdn } from 'utils/assets'
import type { ChecklistRewardType } from 'hooks/use-checklist-rewards'
import { OnboardingV3Context } from 'context/onboarding-v3'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import {
  getVisibleChecklists,
  getFeaturesPermissions
} from 'utils/features-control/features-control'
import * as S from './ModalCompletionOnboardingV3.styles'

const forestImage = Cdn.getImageUrl(
  'onboarding/onboarding-primeira-leitura-sucesso_small.webp'
)
const backgroundImage = Cdn.getImageUrl(
  'onboarding/fundo_feedback_final_sem_fundo.webp'
)
const coinIcon = Cdn.getImageUrl('onboarding/onboarding-coin.svg')
const dropsIcon = Cdn.getImageUrl('onboarding/gotas-recompensa-leitura.svg')
const pointsIcon = Cdn.getImageUrl(
  'onboarding/recompensa-onboarding-pontos.svg'
)

const MODAL_CLASS = 'modal-completion-onboarding-v3'

type ModalCompletionOnboardingV3Props = {
  isOpen: boolean
  onClose: () => void
  onViewConquistas?: () => void
  rewardType?: ChecklistRewardType
  coinsEarned?: number
  dropsEarned?: number
  pointsEarned?: number
}

const getRewardTexts = (
  rewardType?: ChecklistRewardType,
  hasForest: boolean = true
) => {
  switch (rewardType) {
    case 'checklist_primeira_leitura':
      return {
        title: 'Boa! Sua primeira leitura rendeu recompensas!',
        description: hasForest
          ? 'Quanto mais você lê, mais moedas ganha e mais árvores podem ser plantadas na Floresta!'
          : 'Quanto mais você lê, mais moedas ganha! Continue explorando e descubra novas histórias!'
      }
    case 'checklist_area_conquistas':
      return {
        title: 'Parabéns! Você conheceu a área de Conquistas!',
        description: hasForest
          ? 'Continue explorando a Floresta e acompanhe o crescimento das suas árvores!'
          : 'Continue explorando e acompanhe suas conquistas!'
      }
    case 'checklist_plantar_arvore':
      return {
        title: 'Excelente! Você plantou uma árvore!',
        description:
          'Sua árvore está crescendo na Floresta! Continue lendo para plantar mais!'
      }
    default:
      return {
        title: 'Boa! Sua primeira leitura rendeu recompensas!',
        description: hasForest
          ? 'Quanto mais você lê, mais moedas ganha e mais árvores podem ser plantadas na Floresta!'
          : 'Quanto mais você lê, mais moedas ganha! Continue explorando e descubra novas histórias!'
      }
  }
}

const ModalCompletionOnboardingV3 = ({
  isOpen,
  onClose,
  onViewConquistas,
  rewardType,
  coinsEarned = 40,
  dropsEarned = 20,
  pointsEarned = 10
}: ModalCompletionOnboardingV3Props) => {
  const { registerModalOpen, registerModalClosed } =
    React.useContext(OnboardingV3Context)
  const { user } = useUserInfo()

  const visibleChecklists = useMemo(() => getVisibleChecklists(user), [user])
  const featuresPermissions = useMemo(
    () => getFeaturesPermissions(user),
    [user]
  )

  const hasOnlyLeague = useMemo(
    () =>
      featuresPermissions.canAccessLeagueContent &&
      !featuresPermissions.canAccessForest,
    [featuresPermissions]
  )

  const shouldShowConquestsButton = useMemo(
    () => visibleChecklists.showConquests || visibleChecklists.showTree,
    [visibleChecklists]
  )

  const rewardTexts = useMemo(
    () => getRewardTexts(rewardType, visibleChecklists.showTree),
    [rewardType, visibleChecklists.showTree]
  )

  useEffect(() => {
    if (isOpen) {
      registerModalOpen()
      return () => registerModalClosed()
    }
  }, [isOpen, registerModalOpen, registerModalClosed])

  const handleViewConquistas = () => {
    if (onViewConquistas) {
      onViewConquistas()
    } else {
      onClose()
    }
  }

  const modalContainer = document.getElementById('modal')

  return (
    <>
      <S.ModalOverrides />
      {isOpen &&
        modalContainer &&
        createPortal(
          <>
            <S.BackgroundOverlay />
            <S.BackgroundImage src={backgroundImage} alt="" />
          </>,
          modalContainer
        )}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogPortal container={modalContainer}>
          <DialogContent showCloseButton={false} className={MODAL_CLASS}>
            <S.Container>
              <S.CloseButton onClick={onClose} aria-label="Fechar">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>Fechar</title>
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </S.CloseButton>
              <S.ForestIllustration>
                <S.ForestImage src={forestImage} alt="Floresta" />
              </S.ForestIllustration>

              <S.Content>
                <S.TextSection>
                  <S.Title>{rewardTexts.title}</S.Title>
                  {!hasOnlyLeague && <S.Subtitle>Você ganhou:</S.Subtitle>}

                  {!hasOnlyLeague && (
                    <S.RewardContainer>
                      <S.RewardCard>
                        <S.CoinIcon src={coinIcon} alt="Moeda" />
                        <S.RewardText>
                          <S.RewardValue>+{coinsEarned}</S.RewardValue> moedas
                        </S.RewardText>
                      </S.RewardCard>

                      <S.RewardCard>
                        <S.CoinIcon src={dropsIcon} alt="Gotas" />
                        <S.RewardText>
                          <S.RewardValue>+{dropsEarned}</S.RewardValue> gotas
                        </S.RewardText>
                      </S.RewardCard>

                      <S.RewardCard>
                        <S.CoinIcon src={pointsIcon} alt="Pontos" />
                        <S.RewardText>
                          <S.RewardValue>+{pointsEarned}</S.RewardValue> pontos
                        </S.RewardText>
                      </S.RewardCard>
                    </S.RewardContainer>
                  )}

                  <S.Description>{rewardTexts.description}</S.Description>
                </S.TextSection>
              </S.Content>

              <S.ButtonContainer>
                <S.SecondaryButton onClick={onClose}>
                  Agora não
                </S.SecondaryButton>
                {shouldShowConquestsButton && (
                  <S.PrimaryButton onClick={handleViewConquistas}>
                    Ver em Conquistas
                  </S.PrimaryButton>
                )}
              </S.ButtonContainer>
            </S.Container>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  )
}

export default ModalCompletionOnboardingV3
